import type { ICellRendererParams } from 'ag-grid-community'
import type { Color } from '../../../types/colors'
import { iconVars, resolveButtonColors } from '../utils/dom'
import { createTemplateRenderer } from '../utils/fast-renderer'

// Button data type with comprehensive configuration options
type ButtonData = {
  readonly label?: string
  readonly size?: ButtonSize
  readonly variant?: ButtonVariant
  readonly color?: string
  readonly icon?: string
  readonly attrs?: Record<string, string>
}

// Button size options — matches Facade.Tokens.Button.__sizes(): xs | sm | md
type ButtonSize = 'xs' | 'sm' | 'md'

// Button variant options — matches Facade.Tokens.Button.__variants()
type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'ghost'
  | 'neutral'
  | 'unstyled'

const WRAPPER_CLASSES =
  'overflow-hidden w-full whitespace-nowrap h-full inline-flex items-center'

/**
 * Static button skeleton — parsed ONCE, cloned per cell. A wrapper div (layout
 * parity with the other grid renderers) holding a single `<button>` whose
 * variant/size/color/label and Phoenix `phx-*` attributes are written per cell.
 */
const BUTTON_TEMPLATE = `<div class="${WRAPPER_CLASSES}"><button type="button" class="grid-btn"><span class="button-icon" data-component=".icon" hidden><i></i></span><span class="grid-btn__label"></span></button></div>`

// Attributes managed statically on the button — never stripped between refreshes.
const STATIC_BUTTON_ATTRS = new Set(['type', 'class'])

// Only primary/secondary honour a `color` token (matches `<facade-button>`).
const COLORABLE_VARIANTS = new Set<ButtonVariant>(['primary', 'secondary'])

/**
 * Write the resolved colored-variant CSS vars inline on the button. Stale vars
 * from a prior render are already gone (clearDynamicAttrs drops `style`), so
 * this only sets. Mirrors `<facade-button>`'s getColorStyles — the static
 * `.grid-btn--colored` rules in `_grid.css` consume these vars.
 */
const applyColorVars = (button: HTMLElement, color: Color): void => {
  const vars = resolveButtonColors(color)

  for (const [name, value] of Object.entries(vars)) {
    button.style.setProperty(name, value)
  }
}

/**
 * Resolve the phx-target from data.attrs, then context fallbacks.
 * Phoenix LiveView phx-target selectors are safe integers or CSS-like selectors.
 */
const resolvePhxTarget = (
  data: ButtonData,
  params: ICellRendererParams
): string | undefined => {
  if (data.attrs?.['phx-target']) {
    return data.attrs['phx-target']
  }

  const ctx = params.context as Record<string, unknown> | null | undefined

  return (
    (ctx?.phxTarget as string | undefined) ??
    (ctx?.['phx-target'] as string | undefined) ??
    (ctx?.target as string | undefined)
  )
}

/**
 * Strip every dynamic attribute applied by a previous render so a reused button
 * node starts clean before the new data's attributes are written.
 */
const clearDynamicAttrs = (button: HTMLElement): void => {
  for (const name of [...button.getAttributeNames()]) {
    if (!STATIC_BUTTON_ATTRS.has(name)) {
      button.removeAttribute(name)
    }
  }
}

/**
 * Apply button data to a cloned skeleton in place — no re-parse, no re-clone.
 *
 * Execution flow:
 * 1. Clear all dynamic attributes left by any previous render (reused node) —
 *    this also drops any `style="display:none"` left by a prior empty render,
 *    so a reused node carrying data un-hides itself before being restyled.
 * 2. Empty cell → hide the button entirely (blank cell, no stray 1px box).
 * 3. Write variant/size/color as data-* attributes plus the variant/size classes.
 * 4. Write every data.attrs entry (phx-click / phx-value-* …) and the resolved
 *    phx-target so Phoenix's document-level delegation keeps working identically.
 * 5. Toggle the icon span and set the label via textContent (safe — no escaping).
 */
const updateButton = (
  root: HTMLElement,
  data: ButtonData | null | undefined,
  params: ICellRendererParams
): void => {
  const button = root.querySelector('button')!
  const iconEl = root.querySelector('.button-icon')! as HTMLElement
  const labelEl = root.querySelector('.grid-btn__label')!

  clearDynamicAttrs(button)

  if (!data) {
    button.style.display = 'none'
    labelEl.textContent = ''
    iconEl.hidden = true
    button.className = 'grid-btn'
    return
  }

  const variant = data.variant ?? 'secondary'
  const size = data.size ?? 'xs'
  const colored = Boolean(data.color) && COLORABLE_VARIANTS.has(variant)

  button.className = colored
    ? `grid-btn grid-btn--${variant} grid-btn--${size} grid-btn--colored`
    : `grid-btn grid-btn--${variant} grid-btn--${size}`

  button.setAttribute('data-variant', variant)
  button.setAttribute('data-size', size)

  if (data.color) {
    button.setAttribute('data-color', data.color)
  }

  if (colored) {
    applyColorVars(button, data.color as Color)
  }

  const phxTarget = resolvePhxTarget(data, params)

  for (const [key, value] of Object.entries(data.attrs ?? {})) {
    if (key === 'phx-target') {
      continue
    }

    button.setAttribute(key, value)
  }

  if (phxTarget) {
    button.setAttribute('phx-target', phxTarget)
  }

  if (data.icon) {
    const i = iconEl.firstElementChild as HTMLElement
    const vars = iconVars(data.icon, 'xs')

    i.style.setProperty('--icon-size', vars['--icon-size'])
    i.style.setProperty('--icon-url', vars['--icon-url'])
    iconEl.hidden = false
  } else {
    iconEl.hidden = true
  }

  labelEl.textContent = data.label ?? ''
}

/**
 * Button cell renderer — clones a pre-parsed template and writes only dynamic
 * fields/attributes on refresh (no innerHTML re-parse on the hot scroll path).
 *
 * Phoenix LiveView's delegated click handler processes phx-click on any element,
 * so a plain <button> carrying phx-click/phx-target attributes is functionally
 * equivalent to <facade-button> for grid cell interactivity.
 */
export const ButtonComponent = createTemplateRenderer<
  ButtonData | null | undefined
>(BUTTON_TEMPLATE, updateButton)

// Export types for external use
export type { ButtonData, ButtonSize, ButtonVariant }
